import {doppeActionTypeDefineViewerMetadata} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {doppeActionTypeBanner} from "./doppe-action-type-banner";
import React from 'react';
import {
    bymoPageGetInnerNavigatablePageById,
    doppeHideableValueGetVisibleValue,
    DoppeDtoInnerPageType
} from '../../client-server-common';
import {
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl,
    DoppeLinkSpecEntityType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {IDoppeViewerActionContext} from '../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context';

/**
 * Helper function to navigate to a page using the router.
 */
function navigateToPageViaRouter(
    pageId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, bymoPageProps}) => {
        const router = clientServices.viewerRouterClientService;
        const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
        
        if (page) {
            switch (page.type) {
                case DoppeDtoInnerPageType.Products:
                    router.navigateToProductsList();
                    break;
                case DoppeDtoInnerPageType.BlogPosts:
                    router.navigateToBlogPostsList();
                    break;
                case DoppeDtoInnerPageType.Events:
                    router.navigateToEventsList();
                    break;
                case DoppeDtoInnerPageType.Services:
                    router.navigateToServicesList();
                    break;
                case DoppeDtoInnerPageType.Content:
                default:
                    router.navigateToContentPage(page.slug);
                    break;
            }
        }
    });
}

/**
 * Helper function to navigate to an entity item page using the router.
 */
function navigateToEntityViaRouter(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices}) => {
        const router = clientServices.viewerRouterClientService;
        
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product:
                router.navigateToProductItem(entityId);
                break;
            case DoppeLinkSpecEntityType.BlogPost:
                router.navigateToBlogPostItem(entityId);
                break;
            case DoppeLinkSpecEntityType.Event:
                router.navigateToEventItem(entityId);
                break;
            case DoppeLinkSpecEntityType.Service:
                router.navigateToServiceItem(entityId);
                break;
        }
    });
}

export const doppeActionTypeBannerViewer = doppeActionTypeDefineViewerMetadata(doppeActionTypeBanner, {
    loadViewerComponents: async () => {
        return {
            DoppeActionTypeBannerWidget: (await import('./doppe-action-type-banner-widget/doppe-action-type-banner-widget')).DoppeActionTypeBannerWidget
        }
    },

    renderWidget: (renderProps, viewerComponents) => {

        const {
            DoppeActionTypeBannerWidget
        } = viewerComponents;

        return (
            <DoppeActionTypeBannerWidget
                {...renderProps}
            />
        );

    },
    
    getActionButtonProps: actionContext => {
        const action = actionContext.action;
        const linkSpec = doppeHideableValueGetVisibleValue(action.settings.url, null);
        
        // No link or hidden
        if (!linkSpec) {
            return {};
        }
        
        // For Page link specs, navigate via router
        if (doppeLinkSpecIsPage(linkSpec)) {
            const pageId = linkSpec.pageId;
            if (pageId) {
                return {
                    onClick: () => {
                        navigateToPageViaRouter(pageId, actionContext);
                    }
                };
            }
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(linkSpec)) {
            const entityType = linkSpec.entityType;
            const entityId = linkSpec.entityId;
            if (entityType && entityId) {
                return {
                    onClick: () => {
                        navigateToEntityViaRouter(entityType, entityId, actionContext);
                    }
                };
            }
        }
        
        // For URL links, use standard href approach (opens in new tab for banner)
        if (doppeLinkSpecIsUrl(linkSpec)) {
            return {
                href: linkSpec,
                target: '_blank',
                rel: 'noopener',
            };
        }

        return {};
    },
    
    activateCTA: actionContext => {
        const action = actionContext.action;
        const linkSpec = doppeHideableValueGetVisibleValue(action.settings.url, null);
        
        // No link or hidden
        if (!linkSpec) {
            return;
        }
        
        // For Page link specs, navigate via router
        if (doppeLinkSpecIsPage(linkSpec)) {
            const pageId = linkSpec.pageId;
            if (pageId) {
                navigateToPageViaRouter(pageId, actionContext);
            }
            return;
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(linkSpec)) {
            const entityType = linkSpec.entityType;
            const entityId = linkSpec.entityId;
            if (entityType && entityId) {
                navigateToEntityViaRouter(entityType, entityId, actionContext);
            }
            return;
        }
        
        // For URL links, navigate to URL (opens in new tab for banner)
        if (doppeLinkSpecIsUrl(linkSpec)) {
            actionContext.navigateToUrl(linkSpec, true);
        }
    }
})